import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Kanban, List, Calendar, Users, GanttChart, Clock, Archive, RotateCcw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { boardsApi, projectsApi, cardsApi } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { BoardCard, MemberRole, Visibility } from '@/types'
import TaskModal from '@/components/project/TaskModal'
import KanbanView from '@/components/project/KanbanView'
import ListView from '@/components/project/ListView'
import CalendarView from '@/components/project/CalendarView'
import GanttView from '@/components/project/GanttView'
import MemberManagement from '@/components/project/MemberManagement'
import VisibilitySelector, { VisibilityBadge } from '@/components/project/VisibilitySelector'
import { cn } from '@/lib/utils'

type ViewType = 'kanban' | 'list' | 'calendar' | 'gantt'

const VIEW_OPTIONS = [
  { value: 'kanban' as ViewType, label: 'Kanban', icon: Kanban },
  { value: 'list' as ViewType, label: 'List', icon: List },
  { value: 'calendar' as ViewType, label: 'Calendar', icon: Calendar },
  { value: 'gantt' as ViewType, label: 'Gantt', icon: GanttChart },
]

export default function BoardPage() {
  const { boardId } = useParams<{ boardId: string }>()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [currentView, setCurrentView] = useState<ViewType>('kanban')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [hoursPerDay, setHoursPerDay] = useState<string>('8')

  const { data: board, isLoading, error } = useQuery({
    queryKey: ['board', boardId],
    queryFn: () => boardsApi.getById(boardId!),
    enabled: !!boardId,
  })

  // Fetch project data to get project name
  const { data: project } = useQuery({
    queryKey: ['project', board?.project_id],
    queryFn: () => projectsApi.getById(board!.project_id),
    enabled: !!board?.project_id,
  })

  // Sync hoursPerDay state with board data
  useEffect(() => {
    if (board?.hours_per_day) {
      setHoursPerDay(board.hours_per_day.toString())
    }
  }, [board?.hours_per_day])

  // Derive selected card and its list from board data
  const { selectedCard, selectedCardListId } = useMemo(() => {
    if (!selectedCardId || !board?.lists) return { selectedCard: null, selectedCardListId: undefined }
    for (const list of board.lists) {
      const card = list.cards?.find(c => c.card_id === selectedCardId)
      if (card) return { selectedCard: card, selectedCardListId: list.list_id }
    }
    return { selectedCard: null, selectedCardListId: undefined }
  }, [selectedCardId, board?.lists])

  // Handler to select a card by storing its ID
  const handleCardClick = (card: BoardCard) => {
    setSelectedCardId(card.card_id)
  }

  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['board-members', boardId],
    queryFn: () => boardsApi.getMembers(boardId!),
    enabled: !!boardId,
  })

  const { data: archivedCards = [], isLoading: archivedCardsLoading } = useQuery({
    queryKey: ['archivedCards', boardId],
    queryFn: () => boardsApi.getArchivedCards(boardId!),
    enabled: !!boardId && settingsOpen,
  })

  const unarchiveCardMutation = useMutation({
    mutationFn: (cardId: string) => cardsApi.unarchive(cardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      queryClient.invalidateQueries({ queryKey: ['archivedCards', boardId] })
      toast.success('Task restored')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to restore task'
      toast.error(errorMessage)
    },
  })

  const deleteCardPermanentlyMutation = useMutation({
    mutationFn: (cardId: string) => cardsApi.delete(cardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archivedCards', boardId] })
      toast.success('Task permanently deleted')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete task'
      toast.error(errorMessage)
    },
  })

  const updateBoardMutation = useMutation({
    mutationFn: (data: { visibility?: Visibility; hours_per_day?: number }) => boardsApi.update(boardId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      toast.success('Board updated')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update board'
      toast.error(errorMessage)
    },
  })

  const handleAddMember = async (userId: string, role: MemberRole) => {
    await boardsApi.addMember(boardId!, { user_id: userId, member_role: role })
    queryClient.invalidateQueries({ queryKey: ['board-members', boardId] })
  }

  const handleUpdateMember = async (userId: string, role: MemberRole) => {
    await boardsApi.updateMember(boardId!, userId, { member_role: role })
    queryClient.invalidateQueries({ queryKey: ['board-members', boardId] })
  }

  const handleRemoveMember = async (userId: string) => {
    await boardsApi.removeMember(boardId!, userId)
    queryClient.invalidateQueries({ queryKey: ['board-members', boardId] })
  }

  const handleVisibilityChange = (visibility: Visibility) => {
    updateBoardMutation.mutate({ visibility })
  }

  const handleHoursPerDayChange = (value: string) => {
    setHoursPerDay(value)
  }

  const handleHoursPerDaySave = () => {
    const hours = parseFloat(hoursPerDay)
    if (isNaN(hours) || hours <= 0 || hours > 24) {
      toast.error('Hours per day must be between 0.5 and 24')
      return
    }
    updateBoardMutation.mutate({ hours_per_day: hours })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading board...</div>
      </div>
    )
  }

  if (error || !board) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">Failed to load board.</div>
      </div>
    )
  }

  const renderBoardView = () => {
    switch (currentView) {
      case 'list':
        return (
          <ListView
            board={board}
            boardId={boardId!}
            projectName={project?.project_name}
            onCardClick={handleCardClick}
          />
        )
      case 'calendar':
        return (
          <CalendarView
            board={board}
            boardId={boardId!}
            projectName={project?.project_name}
            onCardClick={handleCardClick}
          />
        )
      case 'gantt':
        return (
          <GanttView
            board={board}
            boardId={boardId!}
            onCardClick={handleCardClick}
          />
        )
      case 'kanban':
      default:
        return (
          <KanbanView
            board={board}
            boardId={boardId!}
            projectName={project?.project_name}
            onCardClick={handleCardClick}
          />
        )
    }
  }

  // Check if background is a gradient or solid color
  const isGradient = board.background_color?.startsWith('linear-gradient')
  const backgroundStyle = isGradient
    ? { background: board.background_color }
    : { backgroundColor: board.background_color }

  return (
    <div
      className="absolute inset-0 flex flex-col overflow-hidden"
      style={backgroundStyle}
    >
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 bg-black/30 backdrop-blur-sm border-b border-white/10">
        <div className="flex items-center gap-4">
          <Link to={`/projects/${board.project_id}`}>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-xl font-bold text-white">{board.board_name}</h1>
          <VisibilityBadge visibility={board.visibility} />
        </div>

        <div className="flex items-center gap-3">
          {/* View Switcher */}
          <div className="flex items-center bg-black/20 rounded-lg p-1 gap-1">
            {VIEW_OPTIONS.map((option) => {
              const Icon = option.icon
              const isActive = currentView === option.value
              return (
                <Button
                  key={option.value}
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentView(option.value)}
                  className={cn(
                    "px-3 h-8 text-white/80 hover:text-white hover:bg-white/20",
                    isActive && "bg-white/20 text-white shadow-sm"
                  )}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {option.label}
                </Button>
              )
            })}
          </div>

          {/* Members Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSettingsOpen(true)}
            className="text-white/80 hover:text-white hover:bg-white/20 border border-white/20"
          >
            <Users className="h-4 w-4 mr-2" />
            Members ({members.length})
          </Button>
        </div>
      </div>

      {/* Board Content - Render based on selected view */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {renderBoardView()}
      </div>

      {/* Task Modal for viewing/editing cards */}
      {selectedCard && (
        <TaskModal
          card={selectedCard}
          listId={selectedCardListId}
          lists={board.lists}
          boardId={boardId!}
          projectName={project?.project_name}
          labels={board.labels || []}
          onClose={() => setSelectedCardId(null)}
          mode="edit"
        />
      )}

      {/* Board Settings Sheet */}
      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>Board Settings</SheetTitle>
            <SheetDescription>
              Manage board visibility and members
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <Tabs defaultValue="members">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="members">Members</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
                <TabsTrigger value="archived">
                  <Archive className="h-4 w-4 mr-1" />
                  Archived
                </TabsTrigger>
              </TabsList>
              <TabsContent value="members" className="mt-4">
                <MemberManagement
                  resourceId={boardId!}
                  resourceType="board"
                  members={members}
                  isLoading={membersLoading}
                  onAddMember={handleAddMember}
                  onUpdateMember={handleUpdateMember}
                  onRemoveMember={handleRemoveMember}
                  currentUserId={user?.user_id}
                />
              </TabsContent>
              <TabsContent value="settings" className="mt-4 space-y-6">
                <VisibilitySelector
                  value={board.visibility}
                  onChange={handleVisibilityChange}
                  disabled={updateBoardMutation.isPending}
                />
                <p className="text-sm text-muted-foreground">
                  {board.visibility === 'PUBLIC'
                    ? 'All team members in your organization can view and access this board.'
                    : 'Only members you add to this board can view and access it.'}
                </p>

                {/* Hours per Day Setting */}
                <div className="border-t pt-6">
                  <div className="space-y-2">
                    <Label htmlFor="hours_per_day" className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Hours per Day
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Define how many working hours constitute a day. This is used in the Gantt chart to calculate task duration from effort (e.g., 4 hours effort = 0.5 days if set to 8 hours/day).
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        id="hours_per_day"
                        type="number"
                        min="0.5"
                        max="24"
                        step="0.5"
                        value={hoursPerDay}
                        onChange={(e) => handleHoursPerDayChange(e.target.value)}
                        className="w-24"
                        disabled={updateBoardMutation.isPending}
                      />
                      <span className="text-sm text-muted-foreground">hours</span>
                      <Button
                        size="sm"
                        onClick={handleHoursPerDaySave}
                        disabled={updateBoardMutation.isPending || hoursPerDay === board.hours_per_day?.toString()}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="archived" className="mt-4">
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Archived tasks are hidden from the board but can be restored at any time.
                  </p>
                  {archivedCardsLoading ? (
                    <div className="text-center py-8 text-muted-foreground">Loading...</div>
                  ) : archivedCards.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Archive className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No archived tasks</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {archivedCards.map((card) => (
                        <div
                          key={card.card_id}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{card.card_title}</p>
                            {card.archived_at && (
                              <p className="text-xs text-muted-foreground">
                                Archived {new Date(card.archived_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => unarchiveCardMutation.mutate(card.card_id)}
                              disabled={unarchiveCardMutation.isPending}
                              title="Restore task"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => {
                                if (confirm('Are you sure you want to permanently delete this task? This action cannot be undone.')) {
                                  deleteCardPermanentlyMutation.mutate(card.card_id)
                                }
                              }}
                              disabled={deleteCardPermanentlyMutation.isPending}
                              title="Delete permanently"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

